import * as React from "react";
import { TableCore, TableCoreProps } from "./components/TableCore";
import { ScrollableTableHeader } from "./components/ScrollableTableHeader";
import { Column as _Column } from "react-virtualized";
import { ColumnProps } from "react-virtualized/dist/es/Table";
import { FilterConfig, FilterType, ActiveFilters } from "./ScrollableTable";
import { RadioProps } from "antd/lib/radio";
import { Radio } from "antd";
const Column = _Column as unknown as React.FC<ColumnProps>;

export interface TableWithRadiosColumnProps<T> extends ColumnProps {
  dataKey: keyof T;
  filters?: FilterConfig[];
}

interface TableWithRadiosProps<T> extends TableCoreProps {
  rowKey: keyof T;
  data: T[];
  columns: TableWithRadiosColumnProps<T>[];
  /* Callback executed when row selection was changed */
  onSelectRow?: (selectedRow: T) => void;
  /* Control selection of rows */
  selectedRowKey?: T[keyof T];
  defaultSelectedRowKey?: string | number;
  getRadioProps?: (rowData: T) => RadioProps;
  /* use to omit checking of some specific rows */
  onCheckAllPredicate?: (rowData: T) => boolean;
  onFilter?: (filters: ActiveFilters) => void;
}

interface TableWithRadiosState<T> {
  selectedRow: T;
  selectedRowKey: number | string;
  filters: ActiveFilters;
}

export class TableWithRadios<T> extends React.Component<TableWithRadiosProps<T>, TableWithRadiosState<T>> {
  constructor(props: TableWithRadiosProps<T>) {
    super(props);

    this.state = {
      selectedRow: undefined,
      selectedRowKey: props.defaultSelectedRowKey,
      filters: {},
    };
  }

  componentDidMount() {
    const { defaultSelectedRowKey, data, rowKey } = this.props;
    if (defaultSelectedRowKey) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowData = data.find((item) => item[rowKey] === (defaultSelectedRowKey as any));
      if (rowData) {
        this.setSelectedRows(rowData);
      } else {
        console.warn(`Proper row not found in data for defaultSelectedRowKey = ${defaultSelectedRowKey}`);
      }
    }
  }

  setSelectedRows = (selectedRow: T) => {
    const selectedRowKey = selectedRow[this.props.rowKey as string];
    this.setState({ selectedRow, selectedRowKey }, () => {
      if (typeof this.props.onSelectRow === "function") {
        this.props.onSelectRow(selectedRow);
      }
    });
  };

  radioProps = (rowData: T) => {
    if (typeof this.props.getRadioProps === "function") {
      return this.props.getRadioProps(rowData);
    } else {
      return {};
    }
  };

  handleFilter = () => {
    if (typeof this.props.onFilter === "function") {
      this.props.onFilter(this.state.filters);
    }
  };

  setFilter = (namespace: string, filters: FilterType) => {
    this.setState((prevState) => {
      // when receiving an empty array - remove the namespace from state
      if (!filters.length) {
        const copy = { ...prevState.filters };
        delete copy[namespace];
        return { ...prevState, filters: copy };
      }
      return { ...prevState, filters: { ...prevState.filters, [namespace]: filters } };
    }, this.handleFilter);
  };

  get columns() {
    let columns = this.props.columns || [];
    const { selectedRowKey = this.state.selectedRowKey, onSelectRow } = this.props;
    // if "onSelectRows" callback is set - add row selection checkboxes into the table
    if (typeof onSelectRow === "function") {
      columns.unshift({
        dataKey: this.props.rowKey,
        width: 25,
        flexShrink: 0,
        disableSort: true,
        cellRenderer: ({ cellData, rowData }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Radio
              checked={selectedRowKey === cellData}
              onChange={() => this.setSelectedRows(rowData)}
              {...this.radioProps(rowData)}
            />
          </div>
        ),
        label: null,
      });
    }
    columns = columns.map((item) => ({
      ...item,
      // enhance column props with custom header
      headerRenderer: ({ dataKey, disableSort, label, sortBy, sortDirection }) => (
        <ScrollableTableHeader
          disableSort={disableSort}
          dataKey={dataKey}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label={label as any}
          sortBy={sortBy}
          sortDirection={sortDirection}
          filters={item.filters}
          selectedFilters={this.state.filters[dataKey]}
          onFilter={(filters: FilterType) => this.setFilter(dataKey, filters)}
        />
      ),
    }));
    return columns;
  }

  render() {
    // tslint:disable-next-line: max-line-length
    const {
      rowKey: _rowKey,
      data: _data,
      columns: _columns,
      onSelectRow: _onSelectRow,
      selectedRowKey: _selectedRowKey,
      getRadioProps: _getRadioProps,
      onCheckAllPredicate: _onCheckAllPredicate,
      ...restProps
    } = this.props;

    return (
      <TableCore
        rowGetter={({ index }) => this.props.data[index] || {}}
        ref={this.props.childRef}
        rowClassName={"scrollable-table-row"}
        {...restProps}
      >
        {this.columns.map(({ width: colWidth, dataKey, ...colProps }, idx) => (
          <Column key={`${dataKey.toString()}_${idx}`} dataKey={dataKey as string} {...colProps} width={colWidth} />
        ))}
      </TableCore>
    );
  }
}
